import './Detail.css';

import { useHistory, useParams } from 'react-router-dom';
import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { IonIcon } from '@ionic/react';
import Paginator from '../Paginator/Paginator';
import SlaveTabs from '../SlaveTabs/SlaveTabs';
import DetailContent from '../DetailContent/DetailContent';
import DatasetButtons from '../DatasetButtons/DatasetButtons';
import Button from '../../Button/Button';
import { gridOutline, searchOutline } from 'ionicons/icons';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { Dataset, MatchMode } from '../../../types/types';
import useRoutes from '../../../hooks/useRoutes';
import { useResolvedLabel } from '../../../hooks/useResolvedLabel';
import ComponentSectionContextProvider from '../../../context/ComponentSectionContext';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { useSelectedRowsContext } from '../../../hooks/context/useSelectedRowsContext';
import { useSyncDetailRowToUrl } from './hooks/useSyncDetailRowToUrl';
import { useSyncDetailRowToData } from './hooks/useSyncDetailRowToData';
import { useDetailStepNavigation } from './hooks/useDetailStepNavigation';
import { useDatasetDataRes } from '../../../api/types/types';
import { getSubLabelValue } from './utils/getSubLabelValue';
import { getInitialDetailRowIndex } from './utils/getInitialDetailRowIndex';
import ResponseActionsScopeProvider from '../../../context/ResponseActionsScopeContext';

interface DetailProps {
  isExternalRowDisplay: boolean;
  dataset: Dataset | undefined;
  rows: any;
  datasetData: useDatasetDataRes;
  dataKey: string;
  refetchData: (
    lazy?: boolean,
    queryId?: string,
    activeSorting?: any[],
    activeFilters?: Record<string, unknown>,
    customLimit?: number,
    customOffset?: number
  ) => void;
  openFilter: any;
  nameColumn: any;
  detailColumns: any;
  slaveTabIndex: number;
  setSlaveTabIndex: (value: number) => void;
  expandedRow: any;
  setExpandedRow: (value: any) => void;
  totalRowsCount: number;
  showSelectedRows: boolean;
  loadMoreData: () => void;
  slaveDatasets: any;
  setSlaveDatasets: (value: any) => void;
  slaveLazyStates: any;
  setSlaveLazyStates: (value: any) => void;
  slaveFilters: any;
  setSlaveFilters: (value: any) => void;
  referenceDatasets: any;
  setReferenceDatasets: Dispatch<any>;
  openFullscreenReference: (
    column: any,
    value: string | null,
    matchMode: MatchMode,
    label: string,
    datasetId: string
  ) => void;
}

const Detail = ({
  isExternalRowDisplay,
  dataset,
  rows,
  datasetData,
  dataKey,
  refetchData,
  openFilter,
  nameColumn,
  detailColumns,
  slaveTabIndex,
  setSlaveTabIndex,
  expandedRow,
  setExpandedRow,
  totalRowsCount,
  showSelectedRows,
  loadMoreData,
  slaveDatasets,
  setSlaveDatasets,
  slaveLazyStates,
  setSlaveLazyStates,
  slaveFilters,
  setSlaveFilters,
  referenceDatasets,
  setReferenceDatasets,
  openFullscreenReference,
}: DetailProps) => {
  const { datasetId, itemId, subsetId } = useParams<{
    datasetId: string;
    itemId: string;
    subsetId?: string;
  }>();

  const [currentSelectedRowIndex, setCurrentSelectedRowIndex] = useState(() =>
    getInitialDetailRowIndex(rows, dataKey, expandedRow, itemId)
  );
  //TODO: test if tabIndex is really necessary and if it cannot be unified with slaveTabIndex. Possibly obsolete code.
  const [tabIndex, setTabIndex] = useState(
    slaveTabIndex === 0 ? 1 : slaveTabIndex
  );

  const {
    isDatasetHookLoading,
    activeFilters,
    activeSorting,
    setPeersetFilterFirstProcessed,
    resetDatasetSetup,
  } = useDatasetContext();
  const { selectedVirtualRows } = useSelectedRowsContext();
  const { t } = useTranslate();
  const history = useHistory();
  const routes = useRoutes();
  const resolvedLabel = useResolvedLabel(dataset?.label);

  const currentRow = useMemo(
    () => rows[currentSelectedRowIndex],
    [currentSelectedRowIndex, rows]
  );

  const buildDetailUrlForRow = useCallback(
    (newItemId: string) =>
      subsetId
        ? routes.datasetDetailSubset(datasetId, newItemId, subsetId)
        : routes.datasetDetail(datasetId, newItemId),
    [datasetId, subsetId, routes]
  );

  useSyncDetailRowToUrl({
    rows,
    dataKey,
    expandedRow,
    setExpandedRow,
    setCurrentSelectedRowIndex,
  });

  useSyncDetailRowToData({
    rows,
    dataKey,
    expandedRow,
    isDatasetHookLoading,
    setExpandedRow,
    setCurrentSelectedRowIndex,
    buildDetailUrlForRow,
  });

  const { previousDetail, nextDetail, totalSteps } = useDetailStepNavigation({
    rows,
    dataKey,
    totalRowsCount,
    currentSelectedRowIndex,
    showSelectedRows,
    selectedVirtualRows,
    loadMoreData,
    setExpandedRow,
    setCurrentSelectedRowIndex,
    buildDetailUrlForRow,
  });

  const handleCloseDetail = () => {
    let location: string;

    if (dataset?.subsets) {
      const subsetId = dataset.subsets[tabIndex - 1].datasetId;
      location = routes.subset(datasetId, itemId, subsetId);
    } else {
      location = routes.subsetDetail(datasetId, itemId);
    }

    if (isExternalRowDisplay) {
      setPeersetFilterFirstProcessed(false);
      resetDatasetSetup();
      location = routes.datasetList(datasetId);
    }

    history.push(location);
  };

  const switchTabs = (index) => {
    setTabIndex(index);
    setSlaveTabIndex(index);
  };

  useEffect(() => {
    if (!currentRow) {
      handleCloseDetail();
    }
  }, [currentRow]);

  // Sync initial tabIndex to context so useControlAction reads the correct value on mount.
  useEffect(() => {
    if (tabIndex !== slaveTabIndex) {
      setSlaveTabIndex(tabIndex);
    }
  }, []);

  if (!currentRow || !dataset) {
    return <></>;
  }

  return (
    <>
      <div className="header">
        <ComponentSectionContextProvider componentSection="detail">
          <DatasetButtons
            activeFilters={activeFilters}
            activeSorting={activeSorting}
            data={rows}
            dataset={dataset}
            datasetData={datasetData}
            refetchData={refetchData}
            selectedRows={[currentRow]}
            currentRow={currentRow}
          >
            <Button onClick={handleCloseDetail} fill="clear" round={false}>
              <IonIcon icon={gridOutline} slot="start" />
              {t('list')}
            </Button>
            <Button
              onClick={openFilter}
              className="header-btn"
              fill="clear"
              round={false}
            >
              <IonIcon icon={searchOutline} slot="start" />
              {t('detailedFilter')}
            </Button>
          </DatasetButtons>
        </ComponentSectionContextProvider>
      </div>
      <div className="header">
        <div className="detail-title">
          <h2>{resolvedLabel}</h2>
          <h1>{getSubLabelValue(dataset, currentRow, nameColumn)}</h1>
        </div>

        <div className="right-buttons">
          {rows.length > 1 && (
            <div className="detail-paginator">
              <Paginator
                currentStep={currentSelectedRowIndex + 1}
                numberOfSteps={totalSteps}
                previous={previousDetail}
                next={nextDetail}
              />
            </div>
          )}
        </div>
      </div>
      <DetailContent
        row={currentRow}
        dataset={dataset}
        detailColumns={detailColumns}
        disableInnerScroll
      />
      {dataset.subsets && (
        <ComponentSectionContextProvider componentSection="detail-subset">
          <ResponseActionsScopeProvider scope="subset">
            <SlaveTabs
              masterDataset={dataset}
              parentRow={currentRow}
              isDetail={true}
              detailColumns={detailColumns}
              slaveTabIndex={tabIndex}
              setSlaveTabIndex={switchTabs}
              slaveDatasets={slaveDatasets}
              setSlaveDatasets={setSlaveDatasets}
              slaveLazyStates={slaveLazyStates}
              setSlaveLazyStates={setSlaveLazyStates}
              slaveFilters={slaveFilters}
              setSlaveFilters={setSlaveFilters}
              referenceDatasets={referenceDatasets}
              setReferenceDatasets={setReferenceDatasets}
              openFullscreenReference={openFullscreenReference}
            />
          </ResponseActionsScopeProvider>
        </ComponentSectionContextProvider>
      )}
    </>
  );
};

export default Detail;
